// Start here, /cashflow/start-here (PCFL v3's StartHere, reference/pcfl-v3/source/app.jsx, in the
// hub's frame; no board): the steps to do before Module 1, in order, 1 at a time, with a rail of every
// step and how many are done. A step is ticked done by Next step (welcome text, files, a link or a
// video), by watching its recording, or by ticking all its homework. It opens on the first step
// that isn't done. While staff view as a member, Next step moves on and saves nothing.
import { useState } from 'react'
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { usePhone } from '../../lib/media.js'
import { Link } from '../../lib/router.jsx'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { Tick } from '../../product/Icons.jsx'
import { weekResources } from '../../product/release.js'
import { COPY } from '../../products/ondemand/copy.js'
import { Chip, FileRows, Homework, Md, PageHead } from '../../products/ondemand/parts.jsx'
import { RecordingBlock } from '../../products/ondemand/RecordingBlock.jsx'
import { StepPlayer } from '../../products/ondemand/StepPlayer.jsx'
import { markStep, saveStepPlace, withStepRow } from '../../products/ondemand/data.js'
import { courseModel, stepBody, stepRow, stepTarget } from '../../products/ondemand/model.js'
import { menuLabel } from '../../product/model.js'
import '../../styles/ondemand.css'

export default function OnDemandStartHere({ product }) {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const model = courseModel(data, product)
  const steps = model.startSteps
  const firstOpen = steps.findIndex(s => !model.stepDone(s))
  const [idx, setIdx] = useState(firstOpen === -1 ? Math.max(0, steps.length - 1) : firstOpen)
  const [line, setLine] = useState(null)

  const lede = model.firstNumbered
    ? COPY.startLede(model.firstNumbered.label, model.startDone, model.startTotal)
    : COPY.startLedeAny(model.startDone, model.startTotal)
  if (!steps.length) {
    return <div className="od od-start"><PageHead kicker={COPY.startKicker} title={COPY.startTitle} /></div>
  }

  const step = steps[Math.min(idx, steps.length - 1)]
  const done = model.stepDone(step)
  const last = idx >= steps.length - 1
  const allDone = model.startDone >= model.startTotal
  const left = steps.filter(s => !model.stepDone(s))
  const ticksItself = step.step_type === 'recording' || step.step_type === 'homework'

  function go(i) {
    setLine(null)
    setIdx(i)
    window.scrollTo(0, 0)
  }
  function next() {
    if (!done && !ticksItself && !viewingAs) {
      markStep(me.person.id, step.id, true)
        .then(row => patch(d => withStepRow(d, row)))
        .catch(() => setLine(COPY.saveFailed))
    }
    go(idx + 1)
  }

  return (
    <div className="od od-start">
      <PageHead kicker={COPY.startKicker} title={COPY.startTitle} lede={lede} />
      <nav className="od-rail" aria-label={COPY.startTitle}>
        {steps.map((s, i) => {
          const d = model.stepDone(s)
          return (
            <button key={s.id} type="button" className={`od-rail-step${i === idx ? ' od-rail-step--on' : ''}${d ? ' od-rail-step--done' : ''}`}
              aria-current={i === idx ? 'step' : undefined} onClick={() => go(i)}>
              <b>{d ? <><Tick size={11} stroke={3} />{COPY.stepDoneWord}</> : COPY.stepWord(i + 1)}</b>
              <span>{s.title}</span>
            </button>
          )
        })}
      </nav>
      <section className="od-card od-pad od-step" aria-labelledby="od-step-h">
        <div className="od-kicker">{COPY.stepOf(idx + 1, steps.length, step.config?.optional === true)}</div>
        <h2 className="od-step-h" id="od-step-h">{step.title}</h2>
        <Md text={stepBody(data, step)} />
        <StepWidget key={step.id} step={step} product={product} model={model} />
        {line && <p className="od-msg" role="alert">{line}</p>}
        <div className="od-foot-nav">
          <div>{idx > 0 ? <button type="button" className="od-btn od-btn--quiet" onClick={() => go(idx - 1)}>{COPY.back}</button> : <span />}</div>
          {!last ? (
            <div className="od-foot-right">
              {ticksItself && !done && step.step_type === 'recording' && <span className="od-gate">{COPY.ticksItself}</span>}
              <button type="button" className="od-btn od-btn--primary" onClick={next}>{COPY.nextStep}</button>
            </div>
          ) : allDone ? (
            <div className="od-foot-right">
              <Chip tone="done" big>{COPY.startDone}</Chip>
              <Link className="od-btn od-btn--quiet" to={product.path}>{COPY.backHome}</Link>
            </div>
          ) : (
            <div className="od-foot-right"><span className="od-gate">{COPY.stillToDo(left.map(s => s.title))}</span></div>
          )}
        </div>
      </section>
    </div>
  )
}

// What a step holds besides its words: a video, its recording, its files, a link or its homework
function StepWidget({ step, product, model }) {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const viewingAs = useViewingAs()
  const phone = usePhone()
  const nowMs = useNow()

  if (step.step_type === 'video') {
    const row = stepRow(data, step.id)
    const save = fn => { if (!viewingAs) fn().then(r => patch(d => withStepRow(d, r))).catch(() => { /* the next save tries again */ }) }
    return (
      <StepPlayer title={step.title} url={step.config?.video_url} savedSeconds={row?.position_seconds || 0} watched={Boolean(row?.done_at)}
        watchedShare={Number(data.product?.watched_share) || 0.9} phone={phone}
        onPlace={s => save(() => saveStepPlace(me.person.id, step.id, s))}
        onDone={() => save(() => markStep(me.person.id, step.id, true))} />
    )
  }
  if (step.step_type === 'recording') {
    const rec = (data.recordings || []).find(r => r.id === step.recording_id) || null
    return <RecordingBlock product={product} recording={rec} />
  }
  if (step.step_type === 'files') {
    const files = step.module_id ? weekResources(data, { moduleId: step.module_id }, nowMs) : { sheets: [], prompts: [] }
    const cards = [...files.sheets, ...files.prompts]
    if (!cards.length) return <p className="od-empty">{COPY.noSheets}</p>
    return <FileRows cards={cards} />
  }
  if (step.step_type === 'link') {
    const to = stepTarget(product, step)
    if (!to) return null
    const key = to.split('/').pop()
    return <Link className="od-btn od-btn--quiet od-step-link" to={to}>{COPY.openTarget(menuLabel(key))}</Link>
  }
  if (step.step_type === 'homework') {
    const m = (data.modules || []).find(x => x.id === step.module_id)
    return m ? <Homework module={m} bare /> : null
  }
  return null
}
